import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';
import { ShieldCheck, Search, LogOut, Loader2, Building2, ChevronRight, MapPin } from 'lucide-react';

const STATUS_BADGE = {
  completed: 'bg-[#1B2A7B]/15 text-green-600 border-[#1B2A7B]/30',
  draft: 'bg-[#1B2A7B]/15 text-[#1B2A7B] border-[#1B2A7B]/30',
  not_started: 'bg-slate-500/15 text-slate-500 border-slate-500/30',
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [search, setSearch] = useState('');
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef(null);

  const load = useCallback(async (term) => {
    setLoading(true);
    try {
      const res = await adminAPI.listProperties(term, 60, 0);
      setProperties(res?.STATUS === 1 ? res.RESULT : []);
    } catch (e) {
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(''); }, [load]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(search), 400);
    return () => clearTimeout(debounceRef.current);
  }, [search, load]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin-login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admin-dashboard">
      {/* Header — navy brand strip. iter159 redesign. */}
      <div className="bg-[#1B2A7B] border-b border-[#152163] sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white/15 border border-white/25 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-base leading-tight">BFR Operations</h1>
              <p className="text-[11px] text-white/70">{user?.name} · Read-only</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm px-3 py-1.5 rounded-lg hover:bg-white/10" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 pb-24">
        {/* iter160 — Superhost Calendar and BFR Email tiles removed;
            bottom navigation supersedes them. */}
        {/* iter164 — App Profile Menu module-toggles panel moved out of BFR;
            it belongs to the separate Superhost admin panel. */}

        {/* Search */}
        <div className="relative mb-5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search properties by name, GRB ID, or phone…"
            data-testid="admin-property-search"
            className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm placeholder-slate-500 focus:border-[#1B2A7B] focus:ring-1 focus:ring-[#1B2A7B] outline-none"
          />
        </div>

        <p className="text-xs text-slate-500 mb-3" data-testid="admin-property-count">
          {loading ? 'Loading…' : `${properties.length} properties`}
        </p>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-[#1B2A7B] animate-spin" /></div>
        ) : properties.length === 0 ? (
          <div className="text-center py-20 text-slate-500" data-testid="admin-no-properties">No properties found.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {properties.map((p) => (
              <button
                key={p.property_id}
                onClick={() => navigate(`/admin/property/${p.property_id}`)}
                data-testid={`admin-property-card-${p.property_id}`}
                className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-[#1B2A7B]/50 hover:bg-white/60 transition-colors group"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-[#1B2A7B]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-slate-900 font-semibold text-sm truncate">{p.property_name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{p.grb_id || '—'}</p>
                      {p.owner_name && <p className="text-[11px] text-slate-500 mt-1 truncate">Owner: {p.owner_name}</p>}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-[#1B2A7B] flex-shrink-0 mt-1" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export { STATUS_BADGE };
export default AdminDashboard;
